/**
 * EquipmentSummaryPanel
 * /api/safetyequipment/dashboard-stats/
 *
 * Top: 6 KPI metrics (total, expired, expiring, mfr-expired, mfr-expiring, assigned)
 * Bottom: scrollable list of items needing attention (expired + expiring + no-inspection)
 */
import { getAccessToken } from '../../../auth/tokenStore';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  HardHat, ShieldAlert, ShieldCheck, AlertTriangle,
  Clock, RefreshCw, Loader2, CheckCircle2, XCircle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const fmtDate = s => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
};

const REASON_CFG = {
  expired:               { label: 'Expired',      bg: '#FEE2E2', color: '#DC2626' },
  expiring:              { label: 'Expiring',      bg: '#FEF3C7', color: '#D97706' },
  no_inspection_critical:{ label: 'No inspection', bg: '#FEE2E2', color: '#DC2626' },
};

const Kpi = ({ icon: Icon, label, value, color, sub }) => (
  <div className="flex items-start gap-2 p-2.5 rounded-xl border"
    style={{ backgroundColor: `${color}0D`, borderColor: `${color}25` }}>
    <div className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
      <Icon size={13} style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wide leading-none mb-0.5">{label}</p>
      <p className="text-base font-bold leading-none" style={{ color: value > 0 ? color : '#1e293b' }}>{value}</p>
      {sub && <p className="text-[9px] text-slate-400 mt-0.5 leading-none">{sub}</p>}
    </div>
  </div>
);

const EquipmentSummaryPanel = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${getAccessToken()}` };
    axios.get(`${API_BASE}/api/safetyequipment/dashboard-stats/`, { headers })
      .then(r => setData(r.data))
      .catch(() => setError('Could not load equipment data.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  // Combine alert items: expired → expiring (skip no_inspection_*)
  const alertItems = data ? [
    ...(data.debug_expired_items  || []).filter(i => i.reason === 'expired'),
    ...(data.debug_expiring_items || []),
  ] : [];

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <HardHat size={13} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-800">Equipment Overview</span>
          {!loading && data && alertItems.length > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-500">
              {alertItems.length} alerts
            </span>
          )}
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && data && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* KPI grid */}
          <div className="grid grid-cols-3 gap-1.5 p-2.5 flex-shrink-0">
            <Kpi icon={HardHat}      label="Total"          value={data.total_equipment_count}      color="#3B82F6" />
            <Kpi icon={XCircle}      label="Insp. expired"  value={data.inspection_expired_count}   color="#EF4444" sub="incl. no insp." />
            <Kpi icon={AlertTriangle}label="Insp. expiring" value={data.inspection_expiring_count}  color="#F59E0B" sub="next 30 days" />
            <Kpi icon={ShieldAlert}  label="Mfr. expired"   value={data.manufacturer_expired_count} color="#EF4444" />
            <Kpi icon={Clock}        label="Mfr. expiring"  value={data.manufacturer_expiring_count}color="#F59E0B" />
            <Kpi icon={CheckCircle2} label="Assigned"       value={data.own_active_assignments_count} color="#10B981" />
          </div>

          {/* alert list */}
          {alertItems.length > 0 && (
            <div className="flex-1 min-h-0 flex flex-col border-t border-steel-100">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-steel-50 flex-shrink-0">
                <ShieldAlert size={11} className="text-rose-400" />
                <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Items needing attention</span>
              </div>
              {/* column headers */}
              <div className="flex items-center gap-2 px-3 py-1 border-b border-steel-100 sticky top-0 bg-white z-10">
                <span className="flex-1 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Serial / Type</span>
                <span className="w-16 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Status</span>
                <span className="w-20 text-[9px] font-semibold text-slate-400 uppercase tracking-wide text-right">Validity</span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {alertItems.map((item, i) => {
                  const cfg = REASON_CFG[item.reason] || REASON_CFG.expiring;
                  return (
                    <div key={`${item.reason}-${item.id}`}
                      className={`flex items-center gap-2 px-3 py-1.5 border-b border-steel-50 hover:bg-steel-50 ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-semibold text-slate-700 truncate">
                          {item.serial || `#${item.id}`}
                        </p>
                        <p className="text-[9px] text-slate-400 truncate">{item.type || '—'}</p>
                      </div>
                      <span style={{ backgroundColor: cfg.bg, color: cfg.color }}
                        className="w-16 text-[9px] font-bold px-1.5 py-0.5 rounded-full text-center whitespace-nowrap flex-shrink-0">
                        {cfg.label}
                      </span>
                      <span className="w-20 text-[10px] text-slate-500 text-right flex-shrink-0">{fmtDate(item.validity)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {alertItems.length === 0 && (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
              <ShieldCheck size={28} />
              <span className="text-xs">All equipment OK</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EquipmentSummaryPanel;
